import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import DashboardServiceAPI, { DashboardStats, AgendamentosPorStatus, AtividadeRecente } from '../services/dashboardServiceAPI';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { notificacoesNaoLidas } = useApp();
  const navigate = useNavigate();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statusData, setStatusData] = useState<AgendamentosPorStatus>({});
  const [atividades, setAtividades] = useState<AtividadeRecente[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregarDados = useCallback(async () => {
    try {
      setErro('');
      const [statsData, statusData, ativData] = await Promise.all([
        DashboardServiceAPI.buscarStats().catch(() => null),
        DashboardServiceAPI.buscarAgendamentosPorStatus().catch(() => ({})),
        DashboardServiceAPI.buscarAtividadesRecentes().catch(() => [])
      ]);
      if (statsData) setStats(statsData);
      setStatusData(statusData || {});
      setAtividades(ativData || []);
    } catch (error: any) {
      console.error('Erro ao carregar dashboard:', error);
      setErro('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDados();
    const interval = setInterval(carregarDados, 60000);
    return () => clearInterval(interval);
  }, [carregarDados]);

  const formatarTempo = (data: string) => {
    const diff = Date.now() - new Date(data).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'Agora';
    if (min < 60) return `Há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `Há ${h}h`;
    const d = Math.floor(h / 24);
    return `Há ${d}d`;
  };

  const getCorAtividade = (acao: string) => {
    if (acao.includes('login') || acao.includes('logout')) return { bg: 'bg-blue-100', text: 'text-blue-600' };
    if (acao.includes('create') || acao.includes('criar')) return { bg: 'bg-green-100', text: 'text-green-600' };
    if (acao.includes('delete') || acao.includes('remover')) return { bg: 'bg-red-100', text: 'text-red-600' };
    if (acao.includes('update') || acao.includes('editar')) return { bg: 'bg-yellow-100', text: 'text-yellow-600' };
    return { bg: 'bg-gray-100', text: 'text-gray-600' };
  };

  const statusLabels: Record<string, string> = {
    'Pendente': 'Pendente',
    'Aprovado': 'Aprovado',
    'Reprovado': 'Reprovado',
    'Cancelado': 'Cancelado',
    'Finalizado': 'Finalizado',
    'Em Andamento': 'Em Andamento'
  };

  const statusCores: Record<string, string> = {
    'Pendente': 'bg-yellow-400',
    'Aprovado': 'bg-green-400',
    'Reprovado': 'bg-red-400',
    'Cancelado': 'bg-gray-400',
    'Finalizado': 'bg-blue-400',
    'Em Andamento': 'bg-purple-400'
  };

  const totalStatus = Object.values(statusData).reduce((a, b) => a + b, 0) || 1;

  const cards = [
    { title: 'Agendamentos Hoje', value: stats?.agendamentosHoje ?? 0, icon: '📅', color: 'from-blue-500 to-blue-600' },
    { title: 'Cargas em Andamento', value: stats?.cargasEmAndamento ?? 0, icon: '📦', color: 'from-green-500 to-green-600' },
    { title: 'Solicitações Pendentes', value: stats?.agendamentosPendentes ?? 0, icon: '⏳', color: 'from-yellow-500 to-yellow-600' },
    { title: 'Visitas Hoje', value: stats?.visitasHoje ?? 0, icon: '👥', color: 'from-purple-500 to-purple-600' },
    { title: 'Cargas Finalizadas (7d)', value: stats?.cargasFinalizadas7d ?? 0, icon: '✅', color: 'from-teal-500 to-teal-600' },
    { title: 'Visitas Pendentes', value: stats?.visitasPendentes ?? 0, icon: '📋', color: 'from-orange-500 to-orange-600' },
    { title: 'Notificações Não Lidas', value: notificacoesNaoLidas, icon: '🔔', color: 'from-red-500 to-red-600' },
    { title: 'Filiais Ativas', value: stats?.filiaisAtivas ?? 0, icon: '🏢', color: 'from-indigo-500 to-indigo-600' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500">Bem-vindo(a), {user?.nome}</p>
        {erro && <p className="text-red-500 text-sm mt-1">{erro}</p>}
      </div>

      {/* Cards de indicadores */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {cards.map((card, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className={`bg-gradient-to-br ${card.color} p-3`}>
              <span className="text-2xl">{card.icon}</span>
            </div>
            <div className="p-4">
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              <p className="text-xs text-gray-500 mt-1">{card.title}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gráfico de status */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Agendamentos por Status</h3>
          {Object.keys(statusData).length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nenhum dado disponível</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(statusData).map(([status, count]) => {
                const pct = Math.round(((count as number) / totalStatus) * 100);
                return (
                  <div key={status}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{statusLabels[status] || status}</span>
                      <span className="font-medium text-gray-900">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3">
                      <div
                        className={`${statusCores[status] || 'bg-blue-400'} h-3 rounded-full transition-all duration-500`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Resumo rápido */}
          {stats && (
            <div className="mt-6 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-xl font-bold text-blue-600">{stats.totalAgendamentos}</p>
                <p className="text-xs text-gray-500">Total Agendamentos</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-green-600">{stats.solicitacoesAprovadas}</p>
                <p className="text-xs text-gray-500">Solicitações Aprovadas</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-purple-600">{stats.usuariosAtivos}</p>
                <p className="text-xs text-gray-500">Usuários Ativos</p>
              </div>
            </div>
          )}
        </div>

        {/* Atividades Recentes */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Atividades Recentes</h3>
          {atividades.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nenhuma atividade recente</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {atividades.map((ativ) => {
                const cor = getCorAtividade(ativ.acao);
                return (
                  <div key={ativ.id} className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0">
                    <div className={`flex-shrink-0 ${cor.bg} ${cor.text} rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold`}>
                      {ativ.usuario_nome?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{ativ.usuario_nome || 'Sistema'}</p>
                      <p className="text-xs text-gray-500 truncate">{ativ.descricao || ativ.acao}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{formatarTempo(ativ.data_acao)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Ações Rápidas</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button onClick={() => navigate('/agendamentos')} className="flex items-center gap-2 px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium">
            <span className="text-lg">📅</span> Novo Agendamento
          </button>
          <button onClick={() => navigate('/cargas')} className="flex items-center gap-2 px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium">
            <span className="text-lg">📦</span> Nova Carga
          </button>
          <button onClick={() => navigate('/solicitacoes')} className="flex items-center gap-2 px-4 py-3 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors text-sm font-medium">
            <span className="text-lg">📋</span> Solicitações
          </button>
          <button onClick={() => navigate('/agendar-visitas')} className="flex items-center gap-2 px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium">
            <span className="text-lg">👥</span> Agendar Visita
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
